import { createTheme, ThemeOptions, alpha } from '@mui/material/styles';

const palette = {
  primary: {
    main: '#1B5E20',
    light: '#4C8C4A',
    dark: '#003D00',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#C5A572',
    light: '#F5D99E',
    dark: '#8B7355',
    contrastText: '#000000',
  },
  success: {
    main: '#2E7D32',
    light: '#60AD5E',
    dark: '#005005',
  },
  error: {
    main: '#D32F2F',
    light: '#EF5350',
    dark: '#C62828',
  },
  warning: {
    main: '#ED6C02',
    light: '#FF9800',
    dark: '#E65100',
  },
  info: {
    main: '#0288D1',
    light: '#03A9F4',
    dark: '#01579B',
  },
  background: {
    default: '#F5F5F5',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#212121',
    secondary: '#757575',
  },
};

// Lead with the Apple system stack so iOS Safari renders SF Pro natively;
// Roboto stays in the fallback list so Android and Windows have a known font.
const SYSTEM_FONT_STACK = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"SF Pro Text"',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'system-ui',
  'sans-serif',
].join(',');

const typography = {
  fontFamily: SYSTEM_FONT_STACK,
  h1: {
    fontSize: '3rem',
    fontWeight: 600,
    letterSpacing: '-0.01562em',
  },
  h2: {
    fontSize: '2.5rem',
    fontWeight: 600,
    letterSpacing: '-0.00833em',
  },
  h3: {
    fontSize: '2rem',
    fontWeight: 600,
    letterSpacing: '0em',
  },
  h4: {
    fontSize: '1.75rem',
    fontWeight: 600,
    letterSpacing: '0.00735em',
  },
  h5: {
    fontSize: '1.5rem',
    fontWeight: 600,
    letterSpacing: '0em',
  },
  h6: {
    fontSize: '1.25rem',
    fontWeight: 600,
    letterSpacing: '0.0075em',
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    letterSpacing: '0.00938em',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    letterSpacing: '0.00714em',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    letterSpacing: '0.01071em',
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: 'normal',
    textTransform: 'none' as const,
  },
};

const HAIRLINE = '1px solid rgba(0,0,0,0.08)';

// Apple-flavored press feedback. Replaces MUI's water-drop ripple with a
// brief scale-down + opacity dim, which reads as native tap response.
const PRESS_TRANSFORM = {
  transition:
    'background-color 150ms ease, color 150ms ease, border-color 150ms ease, transform 80ms ease-out, opacity 80ms ease-out',
  '&:active': {
    transform: 'scale(0.97)',
    opacity: 0.85,
  },
};

const components = {
  // Kill the default Material ripple app-wide. We replace it with a scale-down
  // on :active (below) and rely on :focus-visible (browser default + MUI's own)
  // for keyboard accessibility — verified that focus rings still draw without
  // ripple, so this does not regress a11y.
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '10px 24px',
        ...PRESS_TRANSFORM,
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      sizeSmall: {
        minHeight: 36,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: 'none',
        border: HAIRLINE,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
      // Strip the default 1dp/2dp shadows so inline surfaces sit flat. Dialogs,
      // menus, and popovers use elevation 8+ which we leave untouched so modal
      // chrome still feels like it floats above the page.
      elevation1: {
        boxShadow: 'none',
        border: HAIRLINE,
      },
      elevation2: {
        boxShadow: 'none',
        border: HAIRLINE,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: ({ theme }: { theme: ReturnType<typeof createTheme> }) => ({
        boxShadow: 'none',
        borderRadius: 0,
        borderBottom: HAIRLINE,
        // Translucent header with a backdrop-blur so page content shows
        // through the chrome on scroll — the Apple translucent-toolbar look.
        backgroundColor: alpha(theme.palette.primary.main, 0.82),
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }: { theme: ReturnType<typeof createTheme> }) => ({
        borderRadius: 16,
        ...PRESS_TRANSFORM,
        // Soften the saturated MUI-default fills so colored chips read as
        // calm tinted pills rather than candy-bright signals on the
        // forest-green/gold palette.
        '&.MuiChip-filledSuccess': {
          backgroundColor: alpha(theme.palette.success.main, 0.12),
          color: theme.palette.success.dark,
          '& .MuiChip-icon': { color: theme.palette.success.dark },
        },
        '&.MuiChip-filledWarning': {
          backgroundColor: alpha(theme.palette.warning.main, 0.14),
          color: theme.palette.warning.dark,
          '& .MuiChip-icon': { color: theme.palette.warning.dark },
        },
        '&.MuiChip-filledError': {
          backgroundColor: alpha(theme.palette.error.main, 0.12),
          color: theme.palette.error.dark,
          '& .MuiChip-icon': { color: theme.palette.error.dark },
        },
        '&.MuiChip-filledInfo': {
          backgroundColor: alpha(theme.palette.info.main, 0.12),
          color: theme.palette.info.dark,
          '& .MuiChip-icon': { color: theme.palette.info.dark },
        },
      }),
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: '#F5F5F5',
        '& .MuiTableCell-head': {
          fontWeight: 600,
          color: '#424242',
        },
      },
    },
  },
  // Touch-target safety net — iOS HIG asks for 44pt minimum, Material 48dp.
  // Leaves visual icon size alone; just expands the hit area so size="small"
  // IconButtons (commonly used for row actions) become finger-friendly on phones.
  MuiIconButton: {
    styleOverrides: {
      root: {
        minWidth: 44,
        minHeight: 44,
        ...PRESS_TRANSFORM,
      },
    },
  },
};

const themeOptions: ThemeOptions = {
  palette,
  typography,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components: components as any,
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
};

const theme = createTheme(themeOptions);

export default theme;
